import { useCallback, useEffect, useState } from "react";

import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { AnimatePresence, m } from "framer-motion";
import {
  ArrowLeft,
  ArrowRightLeft,
  Building2,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  Loader2,
  Mail,
  Phone,
  Plus,
  Search,
  Stethoscope,
  Trash2,
  UserCircle,
  UserPlus,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PhoneInput } from "@/components/ui/phone-input";
import { useProtectedAction, withProtectedSavingToast } from "@/lib/hooks/use-protected-action";
import { tLocalized, type LocalizedString } from "@/types/localized-string";

/* ------------------------------------------------------------------ */
/* Types — 1:1 port of src/components/billing/seats-section.tsx        */
/* ------------------------------------------------------------------ */

interface SeatAllocation {
  id: string;
  allocationType: string;
  providerId: string | null;
  facilityId: string | null;
  assignedUserId: string | null;
  status: string;
  invitedEmail: string | null;
  invitedPhone: string | null;
  invitedAt: string | null;
  acceptedAt: string | null;
  createdAt: string;
}

interface SeatsData {
  subscription: {
    id: string;
    quantity: number;
    planTier: string;
    status: string;
  };
  allocations: SeatAllocation[];
  seats: { total: number; used: number; pending: number; available: number };
}

interface SubscriptionInfo {
  id: string;
  planTier: string;
  status: string;
  quantity: number;
  plan: {
    code: string;
    name: Record<string, string>;
    features: Record<string, boolean>;
  } | null;
}

type AllocateMode = null | "self" | "facility" | "invite";

interface SearchedProvider {
  id: string;
  name: string;
  photoUrl: string | null;
  city: string | null;
  specialty: Record<string, string> | null;
  email: string | null;
  phone: string | null;
  claimed: boolean;
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                      */
/* ------------------------------------------------------------------ */

function SeatUsageBar({ seats }: { seats: SeatsData["seats"] }) {
  const pct = seats.total > 0 ? (seats.used / seats.total) * 100 : 0;
  const count = seats.available;

  return (
    <div className="mb-4">
      <div className="mb-1.5 flex items-center justify-between text-sm">
        <span className="font-semibold">
          <Trans>Seat usage</Trans>
        </span>
        <span className="text-muted-foreground tabular-nums">
          {seats.used}/{seats.total} <Trans>allocated</Trans>
          {seats.pending > 0 && (
            <span className="ml-1 text-amber-600">
              ({seats.pending} <Trans>pending</Trans>)
            </span>
          )}
        </span>
      </div>
      <div className="bg-muted h-3 overflow-hidden rounded-full">
        <m.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className={`h-full rounded-full ${pct >= 90 ? "bg-amber-500" : "bg-primary"}`}
        />
      </div>
      <div className="text-muted-foreground mt-1.5 text-xs">
        {count > 0 ? (
          <Plural value={count} one="# seat available" other="# seats available" />
        ) : (
          <Trans>All seats allocated. Upgrade for more.</Trans>
        )}
      </div>
    </div>
  );
}

function AllocationRow({
  alloc,
  idx,
  actionLoading,
  onRevoke,
}: {
  alloc: SeatAllocation;
  idx: number;
  actionLoading: string | null;
  onRevoke: (id: string) => void;
}) {
  return (
    <m.div
      key={alloc.id}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ delay: idx * 0.05 }}
      className="flex items-center justify-between rounded-xl border p-3.5"
    >
      <div className="flex items-center gap-3">
        <div className="bg-muted flex h-9 w-9 items-center justify-center rounded-lg">
          {alloc.allocationType === "facility" ? (
            <Building2 className="text-muted-foreground h-4.5 w-4.5" />
          ) : (
            <UserCircle className="text-muted-foreground h-4.5 w-4.5" />
          )}
        </div>
        <div>
          <div className="text-foreground text-sm font-medium">
            {alloc.invitedEmail ||
              alloc.invitedPhone ||
              (alloc.assignedUserId ? <Trans>Your profile</Trans> : null) ||
              alloc.providerId ||
              alloc.facilityId || <Trans>Unassigned</Trans>}
          </div>
          <div className="text-muted-foreground text-xs">
            {alloc.allocationType === "provider" ? <Trans>Doctor</Trans> : <Trans>Location</Trans>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge
          variant="secondary"
          className={
            alloc.status === "active"
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400"
              : "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400"
          }
        >
          {alloc.status === "active" ? (
            <Check className="mr-1 h-3 w-3" />
          ) : (
            <Clock className="mr-1 h-3 w-3" />
          )}
          {alloc.status === "active" ? <Trans>Active</Trans> : <Trans>Pending</Trans>}
        </Badge>
        <button
          type="button"
          onClick={() => onRevoke(alloc.id)}
          disabled={actionLoading === alloc.id}
          className="text-muted-foreground hover:text-destructive min-h-[44px] min-w-[44px] rounded-lg p-1.5 transition-colors"
          aria-label="Revoke"
        >
          {actionLoading === alloc.id ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </button>
      </div>
    </m.div>
  );
}

/* ------------------------------------------------------------------ */
/* AllocatePanel — user-scope allocator (provider seats)               */
/* ------------------------------------------------------------------ */
/* Legacy AllocatePanel supported facility/business scopes via a `scope`
 * prop; the SPA SubscriptionSeats signature drops `scope`, so this port
 * hardcodes the legacy default ("user" scope → provider allocations +
 * /search-providers endpoint). */

const ALLOCATION_TYPE = "provider";
const SEARCH_PATH = "/api/account/seats/search-providers";
const TARGET_ID_FIELD = "providerId";

function AllocatePanel({
  subscriptionId,
  hasProviderId,
  selfAllocationId,
  onDone,
  onRevokeSelf,
  runProtected,
}: {
  subscriptionId: string;
  hasProviderId: boolean;
  selfAllocationId: string | null;
  onDone: () => void;
  onRevokeSelf: (id: string) => void;
  /** SubscriptionSeats' billing PIN gate: re-asks and retries once on a server vault_locked. */
  runProtected: <T>(call: () => Promise<T>) => Promise<T>;
}) {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const navigate = useNavigate();
  const [mode, setMode] = useState<AllocateMode>(null);
  const [submitting, setSubmitting] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchedProvider[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchRan, setSearchRan] = useState(false);

  // Invite fallback state
  const [showInvite, setShowInvite] = useState(false);
  const [inviteKind, setInviteKind] = useState<"email" | "phone">("email");
  const [inviteEmail, setInviteEmail] = useState("");
  const [invitePhone, setInvitePhone] = useState("");
  const [invitePhonePrefix, setInvitePhonePrefix] = useState("+40");
  const [inviteError, setInviteError] = useState<string | null>(null);

  const allocate = useCallback(
    async (body: Record<string, unknown>) => {
      setSubmitting(true);
      try {
        await withProtectedSavingToast(
          () =>
            runProtected(() => apiFetch.post("/api/account/seats", { subscriptionId, ...body })),
          { saving: t`Saving…`, saved: t`Saved`, error: t`Could not save changes` },
        );
        setMode(null);
        setSearchQuery("");
        setSearchResults([]);
        setShowInvite(false);
        setInviteEmail("");
        setInvitePhone("");
        onDone();
      } catch {
        // The toast already said it (or the PIN gate was dismissed).
      } finally {
        setSubmitting(false);
      }
    },
    [subscriptionId, onDone, runProtected, t],
  );

  const handleSelfAllocate = useCallback(() => {
    // "__self__" resolved server-side to the session's own provider entity.
    allocate({ allocationType: ALLOCATION_TYPE, [TARGET_ID_FIELD]: "__self__" });
  }, [allocate]);

  // Debounced provider search.
  useEffect(() => {
    if (mode !== "invite") return;
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearchRan(false);
      return;
    }
    setSearching(true);
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const data = await apiFetch.get<{ results?: SearchedProvider[] }>(
          `${SEARCH_PATH}?q=${encodeURIComponent(q)}`,
          { signal: ctrl.signal },
        );
        setSearchResults(data.results ?? []);
        setSearchRan(true);
      } catch {
        // abort / network — ignore
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [searchQuery, mode]);

  const handleAllocateProvider = useCallback(
    (entityId: string) => {
      allocate({ allocationType: ALLOCATION_TYPE, [TARGET_ID_FIELD]: entityId });
    },
    [allocate],
  );

  const handleSendInvite = useCallback(() => {
    setInviteError(null);
    if (inviteKind === "email") {
      const email = inviteEmail.trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setInviteError(t`Invalid email address`);
        return;
      }
      allocate({ allocationType: ALLOCATION_TYPE, invitedEmail: email });
    } else {
      const digits = invitePhone.replace(/\D/g, "");
      if (digits.length < 6) {
        setInviteError(t`Invalid phone number`);
        return;
      }
      allocate({
        allocationType: ALLOCATION_TYPE,
        invitedPhone: `${invitePhonePrefix}${digits}`,
      });
    }
  }, [allocate, inviteEmail, invitePhone, invitePhonePrefix, inviteKind, t]);

  // Mode selector
  if (mode === null) {
    return (
      <m.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        className="overflow-hidden"
      >
        <div className="space-y-2 rounded-xl border p-4">
          <p className="text-muted-foreground mb-3 text-sm">
            <Trans>How would you like to allocate this license?</Trans>
          </p>

          {/* Self-allocate shortcut — "self" = the signed-in user's Provider
              entity. */}
          {hasProviderId && selfAllocationId && (
            <div className="bg-muted/50 flex min-h-[44px] w-full items-center gap-3 rounded-lg p-3">
              <div className="bg-primary/10 flex h-9 w-9 items-center justify-center rounded-lg">
                <Stethoscope className="text-primary h-4.5 w-4.5" />
              </div>
              <div className="flex-1">
                <div className="text-foreground text-sm font-medium">
                  <Trans>Your profile</Trans>
                </div>
                <div className="text-muted-foreground text-xs">
                  <Trans>You already have a license on this subscription</Trans>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="min-h-[36px]"
                onClick={() => onRevokeSelf(selfAllocationId)}
              >
                <ArrowRightLeft className="mr-1.5 h-3.5 w-3.5" />
                <Trans>Change license</Trans>
              </Button>
            </div>
          )}

          {hasProviderId && !selfAllocationId && (
            <button
              type="button"
              onClick={handleSelfAllocate}
              disabled={submitting}
              className="bg-muted/50 hover:bg-muted flex min-h-[44px] w-full items-center gap-3 rounded-lg p-3 text-left transition-colors"
            >
              <div className="bg-primary/10 flex h-9 w-9 items-center justify-center rounded-lg">
                <Stethoscope className="text-primary h-4.5 w-4.5" />
              </div>
              <div>
                <div className="text-foreground text-sm font-medium">
                  <Trans>Allocate to my doctor profile</Trans>
                </div>
              </div>
              {submitting && <Loader2 className="ml-auto h-4 w-4 animate-spin" />}
            </button>
          )}

          {!hasProviderId && (
            <button
              type="button"
              onClick={() =>
                // Legacy linked to /account/add-provider; in the SPA that flow
                // lives in the `?add=provider` modal mounted on _authenticated.
                navigate({
                  to: ".",
                  search: (prev: Record<string, unknown>) => ({
                    ...prev,
                    add: "provider" as const,
                    aStep: undefined,
                    aMode: undefined,
                  }),
                })
              }
              className="bg-muted/50 hover:bg-muted flex min-h-[44px] w-full items-center gap-3 rounded-lg p-3 text-left transition-colors"
            >
              <div className="bg-primary/10 flex h-9 w-9 items-center justify-center rounded-lg">
                <UserPlus className="text-primary h-4.5 w-4.5" />
              </div>
              <div>
                <div className="text-foreground text-sm font-medium">
                  <Trans>Add your doctor profile</Trans>
                </div>
                <div className="text-muted-foreground text-xs">
                  <Trans>Create a doctor profile to allocate a license to yourself</Trans>
                </div>
              </div>
            </button>
          )}

          <button
            type="button"
            onClick={() => setMode("invite")}
            className="bg-muted/50 hover:bg-muted flex min-h-[44px] w-full items-center gap-3 rounded-lg p-3 text-left transition-colors"
          >
            <div className="bg-primary/10 flex h-9 w-9 items-center justify-center rounded-lg">
              <Mail className="text-primary h-4.5 w-4.5" />
            </div>
            <div>
              <div className="text-foreground text-sm font-medium">
                <Trans>Invite a doctor</Trans>
              </div>
            </div>
          </button>
        </div>
      </m.div>
    );
  }

  // Invite form — search first, fallback to email/phone invite
  return (
    <m.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      className="overflow-hidden"
    >
      <div className="space-y-3 rounded-xl border p-4">
        {/* Back */}
        <button
          type="button"
          onClick={() => {
            setMode(null);
            setShowInvite(false);
            setSearchQuery("");
          }}
          className="text-muted-foreground hover:text-foreground flex min-h-[32px] items-center gap-1 text-xs transition-colors"
        >
          <ArrowLeft className="size-3.5" />
        </button>

        {!showInvite && (
          <>
            {/* Search input */}
            <div className="relative">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t`Search doctor by name, email or phone`}
                autoFocus
                className="bg-background border-border text-foreground focus:ring-primary/30 min-h-[44px] w-full rounded-lg border pr-10 pl-9 text-sm outline-none focus:ring-2"
              />
              {searching && (
                <Loader2 className="text-muted-foreground absolute top-1/2 right-3 size-4 -translate-y-1/2 animate-spin" />
              )}
            </div>

            {/* Search results */}
            {searchQuery.trim().length < 2 && (
              <p className="text-muted-foreground text-center text-xs">
                <Trans>Type at least 2 characters</Trans>
              </p>
            )}

            {searchResults.length > 0 && (
              <div className="space-y-1.5">
                {searchResults.map((doc) => (
                  <div
                    key={doc.id}
                    className="border-border bg-card flex items-center gap-3 rounded-lg border p-3"
                  >
                    <div className="bg-muted flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full">
                      {doc.photoUrl ? (
                        <img src={doc.photoUrl} alt={doc.name} className="size-10 object-cover" />
                      ) : (
                        <UserCircle className="text-muted-foreground size-6" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-foreground truncate text-sm font-medium">{doc.name}</div>
                      <div className="text-muted-foreground truncate text-xs">
                        {[
                          doc.specialty
                            ? doc.specialty[locale] || doc.specialty.en || doc.specialty.ro
                            : null,
                          doc.city,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                      {(doc.email || doc.phone) && (
                        <div className="text-muted-foreground mt-0.5 truncate text-xs">
                          {[doc.email, doc.phone].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleAllocateProvider(doc.id)}
                      disabled={submitting}
                      className="min-h-[36px] shrink-0"
                    >
                      {submitting ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Trans>Allocate</Trans>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {searchRan && !searching && searchResults.length === 0 && (
              <p className="text-muted-foreground text-center text-xs">
                <Trans>No doctor found</Trans>
              </p>
            )}

            {/* Fallback: show invite CTA when search ran but found nothing, or anytime user wants */}
            {searchQuery.trim().length >= 2 && !searching && (
              <button
                type="button"
                onClick={() => setShowInvite(true)}
                className="bg-muted/50 hover:bg-muted flex min-h-[44px] w-full items-center gap-3 rounded-lg p-3 text-left transition-colors"
              >
                <div className="bg-primary/10 flex size-9 items-center justify-center rounded-lg">
                  <Mail className="text-primary size-4" />
                </div>
                <div className="text-foreground text-sm font-medium">
                  <Trans>Can't find the doctor? Send them an invite.</Trans>
                </div>
              </button>
            )}
          </>
        )}

        {showInvite && (
          <>
            {/* Email/Phone toggle */}
            <div className="bg-muted/60 flex rounded-lg p-1">
              <button
                type="button"
                onClick={() => {
                  setInviteKind("email");
                  setInviteError(null);
                }}
                className={cn(
                  "flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                  inviteKind === "email"
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground",
                )}
              >
                <Mail className="size-4" />
                <Trans>Email</Trans>
              </button>
              <button
                type="button"
                onClick={() => {
                  setInviteKind("phone");
                  setInviteError(null);
                }}
                className={cn(
                  "flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                  inviteKind === "phone"
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground",
                )}
              >
                <Phone className="size-4" />
                <Trans>Phone</Trans>
              </button>
            </div>

            {/* Input + send */}
            {inviteKind === "email" ? (
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => {
                  setInviteEmail(e.target.value);
                  setInviteError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSendInvite();
                }}
                placeholder={t`doctor@example.com`}
                autoFocus
                className="bg-background border-border text-foreground focus:ring-primary/30 min-h-[44px] w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:ring-2"
              />
            ) : (
              <PhoneInput
                value={invitePhone}
                onChange={(v) => {
                  setInvitePhone(v);
                  setInviteError(null);
                }}
                defaultCountry="ro"
                onCountryChange={(_code, prefix) => setInvitePhonePrefix(prefix)}
                placeholder={t`712 345 678`}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSendInvite();
                }}
                enterKeyHint="send"
                className="w-full"
              />
            )}

            {inviteError && <p className="text-destructive text-xs">{inviteError}</p>}

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowInvite(false)}
                className="min-h-[44px]"
              >
                <ArrowLeft className="mr-1.5 size-4" />
              </Button>
              <Button
                onClick={handleSendInvite}
                disabled={
                  submitting || (inviteKind === "email" ? !inviteEmail.trim() : !invitePhone.trim())
                }
                className="min-h-[44px] flex-1"
              >
                {submitting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Trans>Send invite</Trans>
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </m.div>
  );
}

/* ------------------------------------------------------------------ */
/* SubscriptionSeats — one card per subscription                       */
/* ------------------------------------------------------------------ */

export function SubscriptionSeats({
  sub,
  hasProviderId,
  userId,
}: {
  sub: SubscriptionInfo;
  hasProviderId: boolean;
  userId?: string;
}) {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showAllocate, setShowAllocate] = useState(false);
  const [expanded, setExpanded] = useState(true);
  // Allocating and revoking seats is billing: the API answers vault_locked
  // while a PIN guarding "billing" is not unlocked — ask, then retry once.
  const { runProtected, PinGateElement } = useProtectedAction("billing");

  const {
    data,
    isPending: loading,
    refetch,
  } = useQuery({
    queryKey: ["account-seats", sub.id],
    queryFn: () =>
      apiFetch.get<SeatsData>(`/api/account/seats?subscriptionId=${encodeURIComponent(sub.id)}`),
  });

  const handleRevoke = useCallback(
    async (allocationId: string) => {
      setActionLoading(allocationId);
      try {
        await withProtectedSavingToast(
          () =>
            runProtected(() =>
              apiFetch.patch("/api/account/seats", { allocationId, action: "revoke" }),
            ),
          { saving: t`Saving…`, saved: t`Saved`, error: t`Could not save changes` },
        );
        await refetch();
      } catch {
        // The toast already said it (or the PIN gate was dismissed).
      } finally {
        setActionLoading(null);
      }
    },
    [refetch, runProtected, t],
  );

  const handleAllocateDone = useCallback(() => {
    setShowAllocate(false);
    refetch();
  }, [refetch]);

  const planName = sub.plan?.name
    ? tLocalized(sub.plan.name as LocalizedString, locale)
    : sub.planTier.charAt(0).toUpperCase() + sub.planTier.slice(1);
  const activeAllocations = data?.allocations.filter((a) => a.status !== "revoked") ?? [];

  // Check if current user's provider already has an active allocation on this subscription
  const selfAllocation = activeAllocations.find(
    (a) => a.assignedUserId === userId && a.status === "active",
  );
  const selfAllocationId = selfAllocation?.id ?? null;

  const count = sub.quantity;

  return (
    <m.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border"
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex min-h-[44px] w-full items-center justify-between p-4 text-left"
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-foreground text-sm font-semibold">
            <Trans>Subscription</Trans> {planName}
          </span>
          <Badge className="bg-primary/10 text-primary hover:bg-primary/15 border-transparent text-xs tabular-nums">
            <Plural value={count} one="# license" other="# licenses" />
          </Badge>
          {sub.status !== "active" && (
            <Badge variant="secondary" className="text-xs">
              {sub.status}
            </Badge>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="text-muted-foreground h-4 w-4" />
        ) : (
          <ChevronDown className="text-muted-foreground h-4 w-4" />
        )}
      </button>

      {/* Collapsible body */}
      <AnimatePresence>
        {expanded && (
          <m.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="space-y-3 border-t px-4 pt-3 pb-4">
              {/* Loading skeleton */}
              {loading && (
                <div className="space-y-2">
                  <div className="bg-muted h-10 animate-pulse rounded-lg" />
                  <div className="bg-muted h-14 animate-pulse rounded-xl" />
                </div>
              )}

              {/* Data loaded */}
              {!loading && data && (
                <>
                  <SeatUsageBar seats={data.seats} />

                  {/* Allocations */}
                  <div className="space-y-2">
                    <AnimatePresence>
                      {activeAllocations.map((alloc, idx) => (
                        <AllocationRow
                          key={alloc.id}
                          alloc={alloc}
                          idx={idx}
                          actionLoading={actionLoading}
                          onRevoke={handleRevoke}
                        />
                      ))}
                    </AnimatePresence>
                  </div>

                  {/* Allocate panel */}
                  <AnimatePresence>
                    {showAllocate && (data.seats.available > 0 || selfAllocationId) && (
                      <AllocatePanel
                        subscriptionId={sub.id}
                        hasProviderId={hasProviderId}
                        selfAllocationId={selfAllocationId}
                        onDone={handleAllocateDone}
                        onRevokeSelf={handleRevoke}
                        runProtected={runProtected}
                      />
                    )}
                  </AnimatePresence>

                  {/* Allocate button */}
                  <Button
                    variant="outline"
                    onClick={() => setShowAllocate(!showAllocate)}
                    disabled={data.seats.available <= 0 && !selfAllocationId}
                    className="min-h-[44px] w-full"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    <Trans>Allocate license</Trans>
                  </Button>

                  {data.seats.available <= 0 && (
                    <p className="text-muted-foreground text-center text-xs">
                      <Trans>Need more licenses? Upgrade your plan.</Trans>
                    </p>
                  )}
                </>
              )}
            </div>
          </m.div>
        )}
      </AnimatePresence>
      {PinGateElement}
    </m.div>
  );
}
